// Vue app
import { createApp } from "vue";
import nwpApp from "./nwp/nwpApp.vue";
import shareApp from "./share/shareApp";

import "@/assets/styles/main.css";
import TrackHelper from "@/helpers/utilities/TrackHelper";

import PiniaStore from "@/stores";
import { i18n, loadLanguageAsync } from "@/helpers/utilities/I18nHelper";

// Helpers
import { nwpRouter, setTranslater } from "./nwp/nwpRouter";
import { shareRouter } from "./share/shareRouter";
import apiGraph from "@/helpers/api/apiGraph";
import apiSkobo from "@/helpers/api/apiSkobo";
import apiUchi from "@/helpers/api/apiUchi";
import apiUchiLog from "@/helpers/api/apiUchiLog";

import * as Tabs from "vue-slim-tabs";

import { SessionHelper } from "@/helpers/utilities/sessionHelper";
import FontAwesomeHelper from "@/helpers/utilities/FontAwesomeHelper";

// Plugins
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { messageState as snackbarMessageState } from "@/components/Shared/GlobalSnackbar.vue";
import { registerDirectives } from "@/directives";

/**********************************************/
// TRACKER
/**********************************************/
TrackHelper.initialize();

/**
 *  Application with login context requiring the user to be invited to the MS Tennant
 */
async function initializeLoggedInContext() {
  try {
    let apiTokenFromQuery;
    let apiToken;

    if (window.location.search.substring(1).startsWith("token=")) {
      apiTokenFromQuery = window.location.search.substring(1).replace("token=", "");
      apiToken = apiTokenFromQuery;
    } else {
      apiToken = SessionHelper.nctSessionToken();
    }

    if (apiToken?.length) {
      // Setup router
      SessionHelper.login(apiToken);
      apiSkobo.init(nwpRouter);
      apiUchi.init(nwpRouter);
      apiUchiLog.init(nwpRouter);

      // Setup pinia
      const pinia = PiniaStore.init();

      /**********************************************/
      // SETUP GRAPH API
      /**********************************************/
      await apiGraph.initGraphApi(
        import.meta.env.VITE_APP_MSGRAPH_API_ENDPOINT,
        import.meta.env.VITE_APP_MSGRAPH_API_ENDPOINT_NEW
      );

      setTranslater(i18n);

      /**********************************************/
      // FONT-AWESOME
      /**********************************************/
      FontAwesomeHelper.initFontAwesome();

      /**********************************************/
      // INIT VUE APP
      /**********************************************/
      const app = createApp(nwpApp);

      app.use(nwpRouter);
      app.use(pinia);
      app.use(i18n);
      app.use(Tabs);

      // Vue Router captures window.location at createRouter() time (module import), before the token
      // is stripped via pushState. Its async initial navigation would re-push /?token=... to the URL
      // when the microtask queue drains (e.g. during the first await below). Replace it now so
      // Vue Router's internal state matches the already-cleaned URL.
      if (apiTokenFromQuery) {
        await nwpRouter.replace({ path: window.location.pathname });
      }

      app.component("FontAwesomeIcon", FontAwesomeIcon);
      registerDirectives(app);

      Object.defineProperty(app.config.globalProperties, "$locale", {
        get: () => i18n.global.locale.value,
        set: locale => {
          i18n.global.locale.value = locale;
        }
      });
      app.config.globalProperties.$skboMessage = snackbarMessageState;
      app.config.globalProperties.$ncProfile = { HideFileExtensions: false };

      TrackHelper.installVueErrorHandler(app);

      app.mount("#app");

      /**********************************************/
      // Forms detection for handling if input has value
      /**********************************************/

      document.addEventListener("change", function (event) {
        let element = event.target;
        if (element && element.matches(".form-element-field")) {
          element.classList[element.value ? "add" : "remove"]("-hasvalue");
        }
      });
    } else {
      // If we have a route but is not actively logged out (eg. we do  not have a system name) redirect to kviklogin
      if (!SessionHelper.customerSystemName) {
        sendToKviklogin();
        return;
      }

      const pinia = PiniaStore.init();

      loadLanguageAsync(import.meta.env.VITE_APP_I18N_FALLBACK_LOCALE, import.meta.env.VITE_APP_I18N_FALLBACK_VARIANT);

      const app = createApp(nwpApp);
      app.use(nwpRouter);
      app.use(pinia);
      app.use(i18n);
      app.mount("#app");
    }
  } catch {
    sendToKviklogin();
  }
}

/**
 * Anonymous application for share through direct files
 */
async function initializeAnonymousContext() {
  try {
    // Setup pinia
    const pinia = PiniaStore.init();

    /**********************************************/
    // FONT-AWESOME
    /**********************************************/
    FontAwesomeHelper.initFontAwesome();

    /**********************************************/
    // INIT VUE APP
    /**********************************************/
    const app = createApp(shareApp);

    app.use(shareRouter);
    app.use(pinia);
    app.use(i18n);

    app.component("FontAwesomeIcon", FontAwesomeIcon);

    app.mount("#app");

    const browserLanguage = navigator.language.substring(0, 1);

    await loadLanguageAsync(browserLanguage == "da" ? "da" : "en", "nwp");

    // eslint-disable-next-line no-empty
  } catch {}
}

const regex = /^(http|https):\/\/[a-z0-9.:]*\/[a-z0-9]{16}$/; // https://regex101.com/r/TI5tKX/2

if (regex.test(window.location.href)) {
  initializeAnonymousContext();
} else {
  initializeLoggedInContext();
}

/**
 *
 */
function sendToKviklogin() {
  SessionHelper.nextLoginRoute = window.location.pathname;
  if (import.meta.env.VITE_APP_NCT_LOGIN_ENDPOINT) {
    window.location.replace(import.meta.env.VITE_APP_NCT_LOGIN_ENDPOINT);
  } else {
    window.location.replace("https://kviklogin.dk");
  }
}
